import { Bone, Matrix4, Quaternion, Vector3 } from 'three';
import type { BVH } from 'three/examples/jsm/loaders/BVHLoader.js';

import { resolvePrimaryPositionTrackName } from '@cartwhl/3d/bvh';

import { KEY_POSE_FK_JOINT_CONFIG_BY_ID } from './fk/keyPoseFkConfig';
import { createKeyPoseSourcePosePayloadFromBvh } from './keyPoseSnapshots';
import type { KeyPoseFkJointId, KeyPoseSourcePosePayload } from './types';

export const INTERVAL_SPACING_JOINT_IDS = [
  'head',
  'left_wrist',
  'right_wrist',
  'spine3',
  'pelvis',
  'left_foot',
  'right_foot',
] as const satisfies readonly KeyPoseFkJointId[];

export type IntervalSpacingJointId = (typeof INTERVAL_SPACING_JOINT_IDS)[number];

export type IntervalSpacingConfig = {
  baseSpacingMultiplier: number;
  maxSpacingMultiplier: number;
  /** Edits at or below this deviation keep base spacing (N frames). */
  subtleDeviationThreshold: number;
  /** Edits at or above this deviation use max spacing (maxMultiplier × N). */
  deviationForMaxSpacing: number;
  /**
   * Scales per-joint rotation deltas (radians) in the combined L2 metric.
   * Position deltas stay at weight 1 (meters).
   */
  rotationWeight: number;
};

export const INTERVAL_SPACING_CONFIG: IntervalSpacingConfig = {
  baseSpacingMultiplier: 1,
  maxSpacingMultiplier: 4,
  // Calibrated from small wrist/arm tweaks landing around 0.3–0.4 deviation.
  subtleDeviationThreshold: 1,
  deviationForMaxSpacing: 4,
  // A 180-degree joint edit contributes ~pi * rotationWeight to the L2 norm.
  rotationWeight: 1.3,
};

const LOG_PREFIX = '[keyPoseIntervalAugmentation]';

export function computeUserEditDeviationFromOriginalMotion({
  bvh,
  userSourcePose,
  config = INTERVAL_SPACING_CONFIG,
}: {
  bvh: BVH;
  userSourcePose: KeyPoseSourcePosePayload;
  config?: Pick<IntervalSpacingConfig, 'rotationWeight'>;
}): number | null {
  const frame = Math.max(Math.round(userSourcePose.frame), 0);
  const originalSourcePose = createKeyPoseSourcePosePayloadFromBvh(bvh, frame);
  if (!originalSourcePose) {
    return null;
  }

  const bones = bvh.skeleton.bones;
  const userWorldPositions = buildBoneWorldPositionMap(bvh, userSourcePose);
  const originalWorldPositions = buildBoneWorldPositionMap(
    bvh,
    originalSourcePose,
  );
  const deltas: number[] = [];
  const rotationWeight = Math.max(config.rotationWeight, 0);

  for (const jointId of INTERVAL_SPACING_JOINT_IDS) {
    const userPosition = resolveSpacingJointWorldPosition(
      jointId,
      userWorldPositions,
    );
    const originalPosition = resolveSpacingJointWorldPosition(
      jointId,
      originalWorldPositions,
    );
    if (userPosition && originalPosition) {
      deltas.push(
        userPosition.x - originalPosition.x,
        userPosition.y - originalPosition.y,
        userPosition.z - originalPosition.z,
      );
    }

    const boneIndex = resolveSpacingJointBoneIndex(jointId, bones);
    if (boneIndex === null) {
      continue;
    }

    const rotationDeltaRadians = quaternionGeodesicDistanceRadians(
      userSourcePose.localJointRot[boneIndex],
      originalSourcePose.localJointRot[boneIndex],
    );
    if (rotationDeltaRadians > 0) {
      deltas.push(rotationWeight * rotationDeltaRadians);
    }
  }

  if (deltas.length === 0) {
    return null;
  }

  return Math.sqrt(deltas.reduce((sum, delta) => sum + delta * delta, 0));
}

export function getRequiredMinFrameSpacingFromDeviation(
  deviation: number,
  intervalFrames: number,
  config: IntervalSpacingConfig = INTERVAL_SPACING_CONFIG,
) {
  const interval = Math.max(Math.round(intervalFrames), 1);
  const normalizedDeviation = Math.max(deviation, 0);

  if (normalizedDeviation <= config.subtleDeviationThreshold) {
    return Math.max(
      Math.round(interval * config.baseSpacingMultiplier),
      1,
    );
  }

  const deviationSpan = Math.max(
    config.deviationForMaxSpacing - config.subtleDeviationThreshold,
    1e-8,
  );
  const interpolation = Math.min(
    (normalizedDeviation - config.subtleDeviationThreshold) / deviationSpan,
    1,
  );
  const spacingMultiplier =
    config.baseSpacingMultiplier +
    interpolation *
      (config.maxSpacingMultiplier - config.baseSpacingMultiplier);

  return Math.max(Math.round(interval * spacingMultiplier), 1);
}

export function getRequiredMinFrameSpacingForUserKeyPose({
  bvh,
  userSourcePose,
  intervalFrames,
  config = INTERVAL_SPACING_CONFIG,
}: {
  bvh: BVH;
  userSourcePose: KeyPoseSourcePosePayload;
  intervalFrames: number;
  config?: IntervalSpacingConfig;
}) {
  const deviation = computeUserEditDeviationFromOriginalMotion({
    bvh,
    userSourcePose,
    config,
  });
  const interval = Math.max(Math.round(intervalFrames), 1);

  if (deviation === null) {
    return Math.max(interval * 2, 1);
  }

  const minSpacing = getRequiredMinFrameSpacingFromDeviation(
    deviation,
    interval,
    config,
  );
  console.log(
    `${LOG_PREFIX} user edit at ${Math.round(userSourcePose.frame)} frame deviation ${deviation.toFixed(4)}, min spacing ${minSpacing} frames`,
  );

  return minSpacing;
}

function buildBoneWorldPositionMap(
  bvh: BVH,
  sourcePose: KeyPoseSourcePosePayload,
) {
  const bones = bvh.skeleton.bones;
  if (!bones.length) {
    return new Map<string, Vector3>();
  }

  const worldMatrices = new Array<Matrix4 | undefined>(bones.length);
  const rootBoneName = resolveRootBoneName(bvh);
  const unitScale = new Vector3(1, 1, 1);

  for (let index = 0; index < bones.length; index += 1) {
    const bone = bones[index];
    const quaternion =
      axisAngleToQuaternion(sourcePose.localJointRot[index]) ??
      new Quaternion();
    const position = new Vector3();
    const isRootBone = bone.name === rootBoneName || bone.parent === null;

    if (isRootBone) {
      position.set(
        sourcePose.rootPosition[0] ?? 0,
        sourcePose.rootPosition[1] ?? 0,
        sourcePose.rootPosition[2] ?? 0,
      );
    } else {
      position.copy(bone.position);
    }

    const localMatrix = new Matrix4().compose(position, quaternion, unitScale);
    const parent = bone.parent;
    const parentIndex =
      parent && parent instanceof Bone ? bones.indexOf(parent) : -1;
    const parentWorldMatrix =
      parentIndex >= 0 ? worldMatrices[parentIndex] : null;

    worldMatrices[index] = parentWorldMatrix
      ? parentWorldMatrix.clone().multiply(localMatrix)
      : localMatrix;
  }

  const worldPositions = new Map<string, Vector3>();
  for (let index = 0; index < bones.length; index += 1) {
    const worldMatrix = worldMatrices[index];
    if (!worldMatrix) {
      continue;
    }

    const position = new Vector3().setFromMatrixPosition(worldMatrix);
    worldPositions.set(bones[index].name, position);
  }

  return worldPositions;
}

function resolveSpacingJointBoneIndex(
  jointId: IntervalSpacingJointId,
  bones: readonly Bone[],
) {
  const boneNames = KEY_POSE_FK_JOINT_CONFIG_BY_ID[jointId].boneNames;
  for (const boneName of boneNames) {
    const boneIndex = bones.findIndex((bone) => bone.name === boneName);
    if (boneIndex >= 0) {
      return boneIndex;
    }
  }

  return null;
}

function quaternionGeodesicDistanceRadians(
  leftAxisAngle?: readonly number[],
  rightAxisAngle?: readonly number[],
) {
  const leftQuaternion = axisAngleToQuaternion(leftAxisAngle) ?? new Quaternion();
  const rightQuaternion =
    axisAngleToQuaternion(rightAxisAngle) ?? new Quaternion();
  const dot = Math.min(1, Math.abs(leftQuaternion.dot(rightQuaternion)));

  return 2 * Math.acos(dot);
}

function resolveSpacingJointWorldPosition(
  jointId: IntervalSpacingJointId,
  worldPositions: ReadonlyMap<string, Vector3>,
) {
  const boneNames = KEY_POSE_FK_JOINT_CONFIG_BY_ID[jointId].boneNames;
  for (const boneName of boneNames) {
    const position = worldPositions.get(boneName);
    if (position) {
      return position;
    }
  }

  return null;
}

function resolveRootBoneName(bvh: BVH) {
  const bones = bvh.skeleton.bones;
  const rootPositionTrackName =
    resolvePrimaryPositionTrackName(bvh) ?? `${bones[0]?.name}.position`;

  return rootPositionTrackName.endsWith('.position')
    ? rootPositionTrackName.slice(0, -'.position'.length)
    : bones[0]?.name ?? null;
}

function axisAngleToQuaternion(axisAngle?: readonly number[]) {
  if (
    !Array.isArray(axisAngle) ||
    axisAngle.length < 3 ||
    !Number.isFinite(axisAngle[0]) ||
    !Number.isFinite(axisAngle[1]) ||
    !Number.isFinite(axisAngle[2])
  ) {
    return null;
  }

  const angle = Math.hypot(axisAngle[0], axisAngle[1], axisAngle[2]);
  if (angle < 1e-8) {
    return new Quaternion();
  }

  return new Quaternion().setFromAxisAngle(
    new Vector3(
      axisAngle[0] / angle,
      axisAngle[1] / angle,
      axisAngle[2] / angle,
    ),
    angle,
  );
}
